import type { BatchLoader } from './createBatchLoader'

// Bounded so long sessions (org switching, ASM impersonation across customers) don't grow this cache forever.
const MAX_SCOPED_LOADERS = 8

/**
 * Caches one `BatchLoader` per scope (e.g. customer+organisation), so an in-flight batch for one scope
 * is unaffected by ambient context (like the selected organisation) changing before it flushes.
 */
export const createScopedLoaderCache = <ScopeArgs extends unknown[], Key, Value>(
  scopeKeyOf: (...args: ScopeArgs) => string,
  createLoader: (...args: ScopeArgs) => BatchLoader<Key, Value>
) => {
  const scopedLoaders = new Map<string, BatchLoader<Key, Value>>()

  return (...args: ScopeArgs): BatchLoader<Key, Value> => {
    const scopeKey = scopeKeyOf(...args)
    const existing = scopedLoaders.get(scopeKey)

    if (existing) {
      // delete-then-set moves this entry to the end of the Map's insertion order (LRU refresh).
      scopedLoaders.delete(scopeKey)
      scopedLoaders.set(scopeKey, existing)

      return existing
    }

    const loader = createLoader(...args)

    if (scopedLoaders.size >= MAX_SCOPED_LOADERS) {
      const oldestScopeKey = scopedLoaders.keys().next().value

      if (oldestScopeKey !== undefined) {
        scopedLoaders.delete(oldestScopeKey)
      }
    }

    scopedLoaders.set(scopeKey, loader)

    return loader
  }
}
